import { RD_PRODUCT_CODE } from '@/app/config/products.config';
import { useCanManualEmission } from '@/shared/hooks';
import { toaster } from '@/shared/ui/toaster.component';
import { useState } from 'react';
import { describeAutomaticEmissionOutcome } from '../utils/automatic-emission-outcome.util';
import { isAwaitingManualEmission } from '../utils/emission-control.util';
import { useIntegrationDetails } from './use-integration-details.hook';
import { useReleaseToAutomaticEmission } from './use-release-to-automatic-emission.hook';

/**
 * Orquestra o "Tentar emissão automática" — a saída para a proposta que ficou
 * esperando emissão manual e que a área prefere emitir pela integração
 * (tipicamente depois de voltar o produto para INTEGRATION).
 *
 * Mesma visibilidade do "Emitir manualmente": Administrador e controle
 * aguardando emissão manual. As duas ações são as duas saídas da mesma espera
 * — exceto no RD Equipamentos, que só tem a saída manual.
 *
 * A tentativa roda na hora e tem três desfechos (ver
 * {@link describeAutomaticEmissionOutcome}). Em todos o modal fecha: o estado do
 * controle mudou, e a tela recarrega com ele. Só o erro da própria requisição
 * (sem permissão, controle fora da espera) fica inline.
 */
export function useReleaseToAutomaticAction({
  policyId,
}: {
  policyId: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { canManualEmission } = useCanManualEmission();
  const { data: control } = useIntegrationDetails(policyId, {
    enabled: canManualEmission === true,
  });

  const mutation = useReleaseToAutomaticEmission();

  // RD Equipamentos tem workflow de emissão próprio, fora deste fluxo: a Api
  // recusa a tentativa, então o botão nem aparece.
  const isVisible =
    canManualEmission === true &&
    isAwaitingManualEmission(control) &&
    control?.productCode !== RD_PRODUCT_CODE;

  const open = () => {
    setErrorMessage(null);
    setIsOpen(true);
  };

  const close = () => {
    if (mutation.isPending) return;
    setErrorMessage(null);
    setIsOpen(false);
  };

  const confirm = async () => {
    setErrorMessage(null);

    try {
      const response = await mutation.mutateAsync(policyId);

      if (!response.success) {
        setErrorMessage(
          response.error || 'Não foi possível tentar a emissão automática.'
        );
        return;
      }

      setIsOpen(false);
      toaster.create(
        describeAutomaticEmissionOutcome(
          response.data?.outcome,
          response.data?.message
        )
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Não foi possível tentar a emissão automática.'
      );
    }
  };

  return {
    isVisible,
    isOpen,
    open,
    close,
    confirm,
    isPending: mutation.isPending,
    errorMessage,
  };
}
